import { useObserveVisibility } from 'App/hooks';
import { useAnalytics } from 'App/Services/Analytics';

import itemFootballTop1920 from '../../../../assets/howplay_img1-1920.png';
import itemFootballCenter1920 from '../../../../assets/howplay_img2-1920.png';
import itemFootballBottom1920 from '../../../../assets/howplay_img3-1920.png';
import itemTextCardLeft from '../../../../assets/howplay_text-card-left.svg';
import itemTextCardRight from '../../../../assets/howplay_text-card-right.svg';
import polyglot from '../../../../localization';
import { LandingAction } from '../LandingAction';

import { HowPlayCard } from './Card';

import './styles.scss';

/**
 * “How to play” section: three illustrated steps with copy cards, a closing subtitle line,
 * and a secondary Play CTA. Uses lazy-loaded imagery and the same `onBtnClick` flow as the hero.
 *
 * @param {string} lang — Active locale key for polyglot strings (titles, step copy, alts).
 * @param {boolean} consentBannerReady — When false, the section Play button stays disabled.
 * @param {() => void} onBtnClick — Handler for the Play CTA (e.g. age gate or phase change from Landing).
 */
const HowPlay = ({ lang, consentBannerReady, onBtnClick }) => {
    const { sendGameDesignEvent, sendGoogleEvent } = useAnalytics();
    const visibleRef = useObserveVisibility(() => {
        sendGameDesignEvent('HP:HowPlayVisible');
        sendGoogleEvent('hp_how_play_visible');
    });

    return (
        <>
            <div className='how-play__bg' />

            <section className='how-play'>
                <div className='how-play__main wrapper'>
                    <div className='container'>
                        <h2 className='how-play__title'>{polyglot.t(`${lang}.howPlay.title`)}</h2>

                        <div className='how-play__items' ref={visibleRef}>
                            <HowPlayCard
                                image={itemFootballTop1920}
                                imageAlt={polyglot.t(`${lang}.howPlay.alt.step1`)}
                                cardImage={itemTextCardRight}
                                cardImageAlt={polyglot.t(`${lang}.howPlay.alt.whiteBack`)}
                                text={polyglot.t(`${lang}.howPlay.scoreGoal`)}
                                align='right'
                                imageClass='top'
                                textClass='1'
                            />

                            <HowPlayCard
                                image={itemFootballCenter1920}
                                imageAlt={polyglot.t(`${lang}.howPlay.alt.step2`)}
                                cardImage={itemTextCardLeft}
                                cardImageAlt={polyglot.t(`${lang}.howPlay.alt.whiteBack`)}
                                text={polyglot.t(`${lang}.howPlay.controlBall`)}
                                align='left'
                                imageClass='center'
                                textClass='2'
                            />

                            <HowPlayCard
                                image={itemFootballBottom1920}
                                imageAlt={polyglot.t(`${lang}.howPlay.alt.step3`)}
                                cardImage={itemTextCardRight}
                                cardImageAlt={polyglot.t(`${lang}.howPlay.alt.whiteBack`)}
                                text={polyglot.t(`${lang}.howPlay.collect`)}
                                align='right'
                                imageClass='bottom'
                                textClass='3'
                            />
                        </div>
                    </div>
                </div>

                <LandingAction
                    lang={lang}
                    disable={!consentBannerReady}
                    onClick={onBtnClick}
                    btnClassName='how-play__btn'
                    textClassName='how-play__btn-text'
                    className='how-play__action'
                    alt={polyglot.t(`${lang}.howPlay.alt.btnPlay`)}>
                    <p className='how-play__action-text'>
                        <span>{polyglot.t(`${lang}.howPlay.subtitle1`)}</span>

                        <span className='how-play__action-text--mark'>
                            {polyglot.t(`${lang}.howPlay.subtitle2`)}
                        </span>

                        <span>{polyglot.t(`${lang}.howPlay.subtitle3`)}</span>
                    </p>
                </LandingAction>
            </section>
        </>
    );
};

export default HowPlay;
